import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import Header from './Header';
import { getUnsubscribeToken } from '../supabase/supabase';
import LoadingLogo from './LoadingLogo';
import { unsubscribe, getMissionaryByUniqueId, validateUnsubscribeToken } from '../api';
import logger from '../logging';

const Unsubscribe = () => {
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [missionaryName, setMissionaryName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [unsubscribeType, setUnsubscribeType] = useState<'single' | 'all'>('single');

    useEffect(() => {
        const emailParam = searchParams.get('email');
        const tokenParam = searchParams.get('token');

        if (emailParam) setEmail(emailParam);
        if (tokenParam) {
            setToken(tokenParam);
            validateToken(tokenParam, emailParam);
        }
    }, [searchParams]);

    const validateToken = async (token: string, email?: string | null) => {
        try {
            const validate = await validateUnsubscribeToken(token, email || '');
            if (!validate) {
                throw new Error('Invalid unsubscribe token');
            }
            setMissionaryName(validate.missionary_name);
        } catch (error) {
            logger.error('Error validating token:', error);
        }
    }

    const handleUnsubscribe = async () => {
        setIsLoading(true);
        try {
            unsubscribe(token, email, unsubscribeType)

            setShowSuccess(true);
        } catch (error) {
            logger.error('Error unsubscribing:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (showSuccess) {
        return (
            <div className="unsubscribe-page">
                <Header />
                <div className="unsubscribe-container">
                    <div className="success-card">
                        <div className="success-icon">✓</div>
                        <h3>Successfully Unsubscribed</h3>
                        <p>
                            {unsubscribeType === 'all'
                                ? 'You have been unsubscribed from all missionary emails.'
                                : `You have been unsubscribed from ${missionaryName || "this missionary's"} emails.`}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="unsubscribe-page">
            <Header />
            <div className="unsubscribe-container">
                {isLoading && <LoadingLogo />}

                {!isLoading && (
                    <div className="unsubscribe-card">
                        <h2>Unsubscribe</h2>
                        <p>
                            We're sorry to see you go. Please confirm your unsubscribe request
                            below.
                        </p>
                        {missionaryName && (
                            <p>
                                <strong>Missionary:</strong> {missionaryName}
                            </p>
                        )}

                        <Form className="unsubscribe-form">
                            <div className="form-group">
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <Form.Check
                                    type="radio"
                                    id="single"
                                    name="unsubscribeType"
                                    label={`Unsubscribe from ${missionaryName || 'this missionary'} only`}
                                    checked={unsubscribeType === 'single'}
                                    onChange={() => setUnsubscribeType('single')}
                                />
                                <Form.Check
                                    type="radio"
                                    id="all"
                                    name="unsubscribeType"
                                    label="Unsubscribe from all missionary emails"
                                    checked={unsubscribeType === 'all'}
                                    onChange={() => setUnsubscribeType('all')}
                                />
                            </div>

                            <Button
                                onClick={handleUnsubscribe}
                                className="unsubscribe-btn"
                                size="lg"
                                disabled={!email}
                            >
                                Unsubscribe
                            </Button>
                        </Form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Unsubscribe;
